import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, ArrowRight, Shield, Eye, EyeOff, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { authApi } from '../lib/api';

const LOGO_MAIN = "https://customer-assets.emergentagent.com/job_stake-blc/artifacts/qx16sddk_Screenshot__247_-removebg-preview%20(1).png";

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: email, 2: code, 3: new password
  const [email, setEmail] = useState('');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const codeInputRefs = useRef([]);

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const handleSendCode = async (e) => {
    e?.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setLoading(true);
    try {
      await authApi.forgotPassword({ email });
      toast.success('Reset code sent to your email');
      setStep(2);
      setResendTimer(60);
      setTimeout(() => codeInputRefs.current[0]?.focus(), 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset code');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);
    if (value && index < 5) {
      codeInputRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyCode = () => {
    const fullCode = code.join('');
    if (fullCode.length !== 6) {
      toast.error('Please enter the complete 6-digit code');
      return;
    }
    setStep(3);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      await authApi.resetPassword({
        email,
        code: code.join(''),
        new_password: newPassword
      });
      toast.success('Password reset successfully!');
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-zinc-900 via-black to-black" />
      <div className="absolute top-1/4 -left-1/4 w-1/2 h-1/2 bg-amber-500/10 rounded-full blur-[120px]" />
      <div className="absolute bottom-1/4 -right-1/4 w-1/2 h-1/2 bg-blue-500/10 rounded-full blur-[120px]" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative"
      >
        <div className="glass-card p-8 border border-white/10">
          {/* Logo */}
          <div className="text-center mb-8">
            <Link to="/">
              <img src={LOGO_MAIN} alt="STAKE BLC" className="h-16 mx-auto mb-4" />
            </Link>
            <h1 className="text-2xl font-bold text-white">Reset Password</h1>
            <p className="text-sm text-zinc-500 mt-2">
              {step === 1 && "Enter your email to receive a reset code"}
              {step === 2 && "Enter the 6-digit code sent to your email"}
              {step === 3 && "Create a new password for your account"}
            </p>
          </div>

          {/* Step 1: Email */}
          {step === 1 && (
            <form onSubmit={handleSendCode} className="space-y-4">
              <div>
                <label className="text-xs text-zinc-400 mb-2 block">Email Address</label>
                <div className="input-glass rounded-lg flex items-center px-4 py-3 gap-3">
                  <Mail className="w-5 h-5 text-zinc-500" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="bg-transparent border-none outline-none text-white w-full placeholder-zinc-600"
                    data-testid="forgot-email"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 rounded-lg gold-btn shadow-lg shadow-amber-500/20 flex items-center justify-center gap-2"
                data-testid="forgot-submit"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                ) : (
                  <>
                    Send Reset Code
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          )}

          {/* Step 2: Code */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-6 h-6 text-amber-400" />
                </div>
                <p className="text-xs text-zinc-500">
                  Code sent to <span className="text-amber-400">{email}</span>
                </p>
              </div>

              <div className="flex justify-center gap-2">
                {code.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => codeInputRefs.current[index] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleCodeKeyDown(index, e)}
                    className="w-11 h-14 text-center text-xl font-bold bg-black/30 border-2 border-white/10 rounded-lg text-white focus:border-amber-500/50 focus:outline-none"
                  />
                ))}
              </div>

              <button
                onClick={handleVerifyCode}
                className="w-full py-3.5 rounded-lg gold-btn shadow-lg shadow-amber-500/20"
              >
                Verify Code
              </button>

              <div className="text-center">
                {resendTimer > 0 ? (
                  <p className="text-xs text-zinc-500">Resend in <span className="text-amber-400">{resendTimer}s</span></p>
                ) : (
                  <button onClick={handleSendCode} className="text-xs text-amber-400 hover:text-amber-300">
                    Resend code
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Step 3: New Password */}
          {step === 3 && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label className="text-xs text-zinc-400 mb-2 block">New Password</label>
                <div className="input-glass rounded-lg flex items-center px-4 py-3 gap-3">
                  <Lock className="w-5 h-5 text-zinc-500" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    className="bg-transparent border-none outline-none text-white w-full placeholder-zinc-600"
                    data-testid="new-password"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <EyeOff className="w-4 h-4 text-zinc-500" /> : <Eye className="w-4 h-4 text-zinc-500" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="text-xs text-zinc-400 mb-2 block">Confirm Password</label>
                <div className="input-glass rounded-lg flex items-center px-4 py-3 gap-3">
                  <KeyRound className="w-5 h-5 text-zinc-500" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    className="bg-transparent border-none outline-none text-white w-full placeholder-zinc-600"
                    data-testid="confirm-new-password"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 rounded-lg gold-btn shadow-lg shadow-amber-500/20 flex items-center justify-center gap-2"
                data-testid="reset-submit"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                ) : (
                  <>
                    Reset Password
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          )}

          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-zinc-400 hover:text-white transition-colors">
              Back to Login
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
